import { METRIC_COLORS } from "../lib/designTokens";
import { useMemo } from "react";
import { computePeakMetrics } from "../lib/peaks";
import { fmtIF, fmtEF } from "../lib/powerMetrics";
import {
  applicableDurations,
  build1HzSeries,
  computePeakCurve,
  computeZoneBreakdown,
  powerZoneBounds,
  hrZoneBounds,
  CADENCE_ZONE_BOUNDS,
  CADENCE_ZONE_NAMES,
  CADENCE_ZONE_COLORS,
  SPEED_ZONE_BOUNDS_KMH,
  SPEED_ZONE_NAMES,
  SPEED_ZONE_COLORS,
  TEMPERATURE_ZONE_BOUNDS,
  TEMPERATURE_ZONE_NAMES,
  TEMPERATURE_ZONE_COLORS,
  POWER_ZONE_NAMES,
  POWER_ZONE_COLORS,
  HR_ZONE_NAMES,
  HR_ZONE_COLORS,
  labelBounds,
} from "../lib/peakCurves";
import { ZonePanel, PeakCurvePanel } from "./MetricAnalysis";
import {
  cadencePresentation,
  isCyclingActivity,
  positiveMetric,
  showCyclingPowerPeaks,
} from "../lib/sportAnalyticsGate";

const dash = "—";

const PairRow = ({ label, value, testId }) => (
  <div
    className="flex items-baseline justify-between gap-3 py-2.5 border-b border-[var(--border-primary)]/60 last:border-b-0"
    data-testid={testId}
  >
    <span className="card-label normal-case tracking-wide">
      {label}
    </span>
    <span className="font-mono text-sm text-dark-text-primary tabular-nums">
      {value ?? dash}
    </span>
  </div>
);

export const PeaksView = ({
  activity,
  samples,
  userFtp = null,
  userWeightKg = null,
  userMaxHr = null,
}) => {
  const peaks = useMemo(
    () =>
      computePeakMetrics(activity, samples, {
        ftp: userFtp,
        weightKg: userWeightKg,
      }),
    [activity, samples, userFtp, userWeightKg]
  );

  const startTime = activity.start_time;

  const power1Hz = useMemo(
    () => build1HzSeries(samples, "power", startTime),
    [samples, startTime]
  );
  const hr1Hz = useMemo(
    () => build1HzSeries(samples, "hr", startTime),
    [samples, startTime]
  );
  const cadence1Hz = useMemo(
    () => build1HzSeries(samples, "cadence", startTime),
    [samples, startTime]
  );
  const speedKmh1Hz = useMemo(
    () => build1HzSeries(samples, "speed_ms", startTime, (v) => v * 3.6),
    [samples, startTime]
  );
  const temp1Hz = useMemo(
    () => build1HzSeries(samples, "temperature_c", startTime),
    [samples, startTime]
  );

  const totalSec =
    activity.duration_seconds ||
    power1Hz.totalSec ||
    hr1Hz.totalSec ||
    speedKmh1Hz.totalSec;
  const durations = useMemo(
    () => applicableDurations(totalSec),
    [totalSec]
  );

  const powerCurve = useMemo(
    () => (activity.has_power ? computePeakCurve(power1Hz.series, durations) : []),
    [activity.has_power, power1Hz.series, durations]
  );
  const hrCurve = useMemo(
    () => (activity.has_hr ? computePeakCurve(hr1Hz.series, durations) : []),
    [activity.has_hr, hr1Hz.series, durations]
  );
  const cadenceCurve = useMemo(
    () => (activity.has_cadence ? computePeakCurve(cadence1Hz.series, durations) : []),
    [activity.has_cadence, cadence1Hz.series, durations]
  );
  const speedCurve = useMemo(
    () => computePeakCurve(speedKmh1Hz.series, durations),
    [speedKmh1Hz.series, durations]
  );
  const tempCurve = useMemo(
    () =>
      peaks.has_temperature
        ? computePeakCurve(temp1Hz.series, durations, { floorBest: -Infinity })
        : [],
    [peaks.has_temperature, temp1Hz.series, durations]
  );

  const powerZones = useMemo(() => {
    if (!activity.has_power) return [];
    const bounds = powerZoneBounds(userFtp);
    const ranges = labelBounds(bounds, "W");
    const secs = computeZoneBreakdown(power1Hz.series, bounds);
    return secs.map((s, i) => ({
      name: POWER_ZONE_NAMES[i],
      range: ranges[i],
      seconds: s,
      color: POWER_ZONE_COLORS[i],
    }));
  }, [activity.has_power, power1Hz.series, userFtp]);

  const hrZones = useMemo(() => {
    if (!activity.has_hr) return [];
    const bounds = hrZoneBounds(userMaxHr);
    const ranges = labelBounds(bounds, "bpm");
    const secs = computeZoneBreakdown(hr1Hz.series, bounds);
    return secs.map((s, i) => ({
      name: HR_ZONE_NAMES[i],
      range: ranges[i],
      seconds: s,
      color: HR_ZONE_COLORS[i],
    }));
  }, [activity.has_hr, hr1Hz.series, userMaxHr]);

  const cadenceZones = useMemo(() => {
    if (!activity.has_cadence) return [];
    const secs = computeZoneBreakdown(cadence1Hz.series, CADENCE_ZONE_BOUNDS);
    return secs.map((s, i) => ({
      name: CADENCE_ZONE_NAMES[i],
      range: `${CADENCE_ZONE_NAMES[i]} rpm`,
      seconds: s,
      color: CADENCE_ZONE_COLORS[i],
    }));
  }, [activity.has_cadence, cadence1Hz.series]);

  const speedZones = useMemo(() => {
    const hasAnySpeed = speedKmh1Hz.series.some((v) => v != null && v > 0);
    if (!hasAnySpeed) return [];
    const secs = computeZoneBreakdown(speedKmh1Hz.series, SPEED_ZONE_BOUNDS_KMH);
    return secs.map((s, i) => ({
      name: SPEED_ZONE_NAMES[i],
      range: `${SPEED_ZONE_NAMES[i]} km/h`,
      seconds: s,
      color: SPEED_ZONE_COLORS[i],
    }));
  }, [speedKmh1Hz.series]);

  const tempZones = useMemo(() => {
    if (!peaks.has_temperature) return [];
    const secs = computeZoneBreakdown(temp1Hz.series, TEMPERATURE_ZONE_BOUNDS);
    return secs.map((s, i) => ({
      name: TEMPERATURE_ZONE_NAMES[i],
      range: `${TEMPERATURE_ZONE_NAMES[i]} °C`,
      seconds: s,
      color: TEMPERATURE_ZONE_COLORS[i],
    }));
  }, [peaks.has_temperature, temp1Hz.series]);

  return (
    <div className="space-y-6" data-testid="peaks-view">
      {/* Paired metrics table */}
      <div className="app-card-elevated p-5 sm:p-6">
        <div className="card-label">Entire Workout</div>
        <h3 className="section-title mt-0.5">Peak Metrics</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 mt-4">
          <div>
            <PairRow
              label="Work"
              value={peaks.work_kj != null ? `${peaks.work_kj} kJ` : null}
              testId="peaks-work"
            />
            <PairRow
              label="NP"
              value={peaks.np != null ? `${peaks.np} W` : null}
              testId="peaks-np"
            />
            <PairRow
              label="Pw:Hr"
              value={
                peaks.pw_hr_pct != null
                  ? `${peaks.pw_hr_pct.toFixed(2)}%`
                  : null
              }
              testId="peaks-pwhr"
            />
            <PairRow
              label="El. Gain"
              value={peaks.elevation_gain_m != null ? `${peaks.elevation_gain_m} m` : null}
              testId="peaks-el-gain"
            />
            <PairRow
              label="El. Loss"
              value={peaks.elevation_loss_m != null ? `${peaks.elevation_loss_m} m` : null}
              testId="peaks-el-loss"
            />
            {showCyclingPowerPeaks(activity) && positiveMetric(peaks.w_per_kg) ? (
            <PairRow
              label="W/kg"
              value={peaks.w_per_kg.toFixed(2)}
              testId="peaks-wkg"
            />
            ) : null}
            {peaks.has_temperature && (
              <>
                <PairRow
                  label="Min Temp"
                  value={
                    peaks.min_temperature_c != null
                      ? `${peaks.min_temperature_c} °C`
                      : null
                  }
                  testId="peaks-min-temp"
                />
                <PairRow
                  label="Avg Temp"
                  value={
                    peaks.avg_temperature_c != null
                      ? `${peaks.avg_temperature_c} °C`
                      : null
                  }
                  testId="peaks-avg-temp"
                />
                <PairRow
                  label="Max Temp"
                  value={
                    peaks.max_temperature_c != null
                      ? `${peaks.max_temperature_c} °C`
                      : null
                  }
                  testId="peaks-max-temp"
                />
              </>
            )}
          </div>
          <div>
            <PairRow
              label="IF"
              value={peaks.intensity_factor != null ? fmtIF(peaks.intensity_factor) : null}
              testId="peaks-if"
            />
            <PairRow
              label="VI"
              value={peaks.vi != null ? peaks.vi.toFixed(2) : null}
              testId="peaks-vi"
            />
            <PairRow
              label="EF"
              value={peaks.ef != null ? fmtEF(peaks.ef) : null}
              testId="peaks-ef"
            />
            <PairRow
              label="Grade"
              value={peaks.grade_pct != null ? `${peaks.grade_pct.toFixed(1)}%` : null}
              testId="peaks-grade"
            />
            <PairRow
              label="VAM"
              value={peaks.vam_mhr != null ? `${peaks.vam_mhr} m/hr` : null}
              testId="peaks-vam"
            />
          </div>
        </div>
      </div>

      {/* Metric analysis rows: zones + peak curve */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {showCyclingPowerPeaks(activity) && powerZones.length > 0 && (
          <ZonePanel
            title="Power"
            unit="W"
            zones={powerZones}
            testId="zone-panel-power"
          />
        )}
        {showCyclingPowerPeaks(activity) && powerCurve.length > 0 && (
          <PeakCurvePanel
            title="Power"
            unit="W"
            curve={powerCurve}
            color={METRIC_COLORS.power}
            testId="peak-panel-power"
            weightKg={userWeightKg}
          />
        )}

        {hrZones.length > 0 && (
          <ZonePanel
            title="Heart Rate"
            unit="bpm"
            zones={hrZones}
            testId="zone-panel-hr"
          />
        )}
        {hrCurve.length > 0 && (
          <PeakCurvePanel
            title="Heart Rate"
            unit="bpm"
            curve={hrCurve}
            color={METRIC_COLORS.hr}
            testId="peak-panel-hr"
          />
        )}

        {isCyclingActivity(activity) && cadencePresentation(activity).show && cadenceZones.length > 0 && (
          <ZonePanel
            title="Cadence"
            unit="rpm"
            zones={cadenceZones}
            testId="zone-panel-cadence"
          />
        )}
        {isCyclingActivity(activity) && cadencePresentation(activity).show && cadenceCurve.length > 0 && (
          <PeakCurvePanel
            title="Cadence"
            unit="rpm"
            curve={cadenceCurve}
            color={METRIC_COLORS.cadence}
            testId="peak-panel-cadence"
          />
        )}

        {speedZones.length > 0 && (
          <ZonePanel
            title="Speed"
            unit="km/h"
            zones={speedZones}
            testId="zone-panel-speed"
          />
        )}
        {speedCurve.length > 0 && (
          <PeakCurvePanel
            title="Speed"
            unit="km/h"
            curve={speedCurve}
            color={METRIC_COLORS.speed}
            testId="peak-panel-speed"
          />
        )}

        {tempZones.length > 0 && (
          <ZonePanel
            title="Temperature"
            unit="°C"
            zones={tempZones}
            testId="zone-panel-temperature"
          />
        )}
        {tempCurve.length > 0 && (
          <PeakCurvePanel
            title="Temperature"
            unit="°C"
            curve={tempCurve}
            color={METRIC_COLORS.temperature}
            testId="peak-panel-temperature"
            valueDecimals={1}
          />
        )}
      </div>

      {showCyclingPowerPeaks(activity) && !positiveMetric(userFtp) && (
        <p className="text-xs text-dark-text-secondary px-1">
          Set your <a href="/settings" className="text-analytics-amber hover:underline">FTP</a>{" "}
          in Settings to enable IF, TSS, W/kg and refine power zones.
        </p>
      )}
      {showCyclingPowerPeaks(activity) && positiveMetric(userFtp) && !positiveMetric(userWeightKg) && (
        <p className="text-xs text-dark-text-secondary px-1">
          Set your <a href="/settings" className="text-analytics-amber hover:underline">weight</a>{" "}
          in Settings to see W/kg on peak power intervals.
        </p>
      )}
    </div>
  );
};
